/**
 * Geometry and randomisation shared by the puzzle set.
 *
 * The pixel constants come straight from the Claude Design source
 * ("Captcha Puzzle Concepts", variants 1a/1c/1d/2b/2c/2f/2h/2j/2k/2o) and are load-bearing:
 * hit-testing is done against them, so nudging one to "tidy up" the layout silently changes
 * whether a drop counts as solved.
 */

/** Playfield is a fixed 312px so the four 66px bays land on a whole-pixel grid. */
export const SCENE_WIDTH = 312;

/** Left edge of each of the four parking bays. */
export const BAY_LEFTS = [12, 86, 160, 234] as const;

/** Left edge of each of the three gates (2c) — wider spacing than the bays. */
export const GATE_LEFTS = [24, 123, 222] as const;

export const BAY_WIDTH = 66;
export const CAR_WIDTH = 46;
export const CAR_HEIGHT = 72;

/**
 * Named vehicle colours for the choose-a-vehicle puzzles (2h, 2k, 2o).
 *
 * ## Invariants:
 * - These are ANSWER CONTENT, not styling, and must NOT be swapped for theme tokens — the one
 *   deliberate exception to this app's white-label rule. Why: 2k and 2o put the colour name in
 *   the prompt ("Tap the green car"). Point any entry at the tenant's primary and a company
 *   whose brand is blue gets "Tap the green car" over a blue vehicle — no correct answer exists,
 *   and the genuinely green decoy beside it now looks right. Every surrounding element (the
 *   draggable car, barrier, slider handle, target bay, chrome, wheels, glass) DOES follow the
 *   theme; the answers cannot.
 * - Five distinct, plainly-nameable hues, each far enough apart to survive a small SVG on a
 *   phone. Why: a customer has to map a word to a shape a few millimetres wide, so near-hues
 *   (navy vs. blue) would make a correct tap a coin flip.
 * - The names are translation keys (`color_blue`), not display strings. Why: the prompt reads
 *   "Tap the {color} car", so the one meaningful word would otherwise stay English in every
 *   other locale.
 */
export const VEHICLE_COLORS = [
  { name: 'blue', hex: '#2F6FDE' },
  { name: 'red', hex: '#C7321E' },
  { name: 'green', hex: '#128A4D' },
  { name: 'gray', hex: '#6B7A90' },
  { name: 'yellow', hex: '#D9930D' },
] as const;

export type VehicleColor = (typeof VEHICLE_COLORS)[number];

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const randomInt = (bound: number): number => Math.floor(Math.random() * bound);

/** Fisher–Yates. Returns a new array; never mutates the input. */
export function shuffle<T>(items: readonly T[]): T[] {
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Picks one element at random. Callers guarantee a non-empty list. */
export function pickOne<T>(items: readonly T[]): T {
  return items[randomInt(items.length)];
}
